import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { setDisplayDateFormat } from '@/shared/format/pbDateFormat';

/**
 * App-shell feature flags. Mirrors backend FeatureFlags record returned by
 * GET /api/config/features. Cached for the lifetime of the SPA session
 * (staleTime: Infinity) -- flags don't change without a tenant config update,
 * and a reload picks up the change.
 */
export interface FeatureFlags {
  ancientDbEnabled: boolean;
  /** slice-2950: PB gs_mode — the client deployment mode (rf_configuration
   *  Client/Mode, default 'STD'). PB gates mode-specific features on it, e.g.
   *  w_order_search enables Set Delivery / Tag / Untag only when gs_mode='KCC'. */
  clientMode?: string;
  /** Chris 2026-08-05: PB's General/ElectronicSignatureEnable CRT setting. w_warehouse
   *  registers Load Signature DISABLED (srw:514) and enables it only when this is 'Y'
   *  AND an existing warehouse is open (srw:523-527). Defaults to false so a failed
   *  probe leaves the control disabled rather than live. */
  electronicSignatureEnabled?: boolean;
  /** Slice follow-up to docs/audits/add-rowcount-total-count.md (orchestrator note,
   *  2026-08-27): the caller's OWN dbxx.datawin_option rows — every PB DataWindow name with
   *  (window_name='(null)', add_rowcount='Y') in the tenant's own client DB. PB reads this
   *  per client at DataWindow-control construction (uo_phfc_global_dw_buffers.sru:164-172);
   *  every {@link RowCountFooter} mount site gates on its own dataobject name being IN this
   *  list, not on a static fleet-wide opt-in — the set is identical in five of six client DBs
   *  sampled but JWP_PROD900 carries one extra row (dw_verify_w_ship_content). Defaults to an
   *  empty array so nothing renders before this loads (no flash) or on a failed probe. */
  addRowcount?: string[];
  /** BigList #8 (docs/audits/biglist/08-date-format.md): PB's raw, un-mapped
   *  `DisplayDateFormat` token (e.g. `'MM/dd/yy'`) — `rf_configuration` 'General'/
   *  'DisplayDateFormat', PB's own `'mm/dd/yyyy'`-family fallback when missing/blank.
   *  Un-mapped by design: {@link setDisplayDateFormat} owns the PB-token -> display-pattern
   *  mapping, so it lives in exactly one place. Consumed via {@link useSyncDisplayDateFormat},
   *  not read directly by feature code — read `getDisplayDateFormat()` from
   *  `shared/format/pbDateFormat` instead. */
  displayDateFormat?: string;
  /** slice-3554 (BigList item 11, Chris 2026-09-04): the tenant-wide Max-results row cap for
   *  grid screens — the default the dropdown opens on plus the choices it offers. Two rows in
   *  the client's own dbxx.rf_configuration (section 'PH10', parms 'MaxResultsDefault' /
   *  'MaxResultsOptions'), edited in the existing Configuration editor at /browse/configuration.
   *  `options` is the authority for BOTH the dropdown contents and what the browse endpoint's
   *  reserved `_maxResults` param accepts (anything else is a 422); `defaultValue` is guaranteed
   *  by the BE to be one of them. Consume via useMaxResults(), never directly — that hook layers
   *  the operator's remembered pick on top. */
  maxResults?: {
    /** A numeric string or the literal 'ALL'. */
    defaultValue: string;
    /** Numeric strings and/or 'ALL', in the admin's configured display order. */
    options: string[];
  };
  /** slice-3560 (BigList #19, ADR-0046): the tenant's session-timeout policy, read from
   *  rf_configuration section 'PH10' by SessionPolicy. Drives {@link useSessionExpiry}: when
   *  to warn, how long the countdown runs, whether it chimes, and (with the token TTL) how
   *  often to renew. Never absent from a live BE — {@link SESSION_DEFAULTS} covers the
   *  in-flight first render and an older jar. */
  session?: Partial<SessionFeatureFlags>;
  /** slice-3609: false on the customer DEMO instance — hides every testers-hub surface. */
  testersHubEnabled?: boolean;
  /** slice-3626 (ADR-0050): customer DEMO instance — Slingshot labor reports + RF Simulator. */
  demoSystem?: boolean;
  /** slice-3930: TEMPORARY "My top screens" tester survey (ph10.topscreens.enabled). */
  topScreensEnabled?: boolean;
  /** slice-4446: this box's RF Simulator MQTT instance (ph10.rf-simulator.instance); blank = built-in. */
  rfSimInstance?: string;
  /** slice-4482: RF Simulator on a non-demo box (ph10.rf-simulator.enabled; ph10-test). */
  rfSimEnabled?: boolean;
  /** slice-4482: this box's RF Simulator Mover ID (ph10.rf-simulator.mover); blank = built-in. */
  rfSimMover?: string;
}

/** FE-visible half of `com.qssi.ph10.platform.config.SessionSettings`. */
export interface SessionFeatureFlags {
  /** Minutes of inactivity that end the session. */
  idleMinutes: number;
  /** Seconds before that limit at which the warning modal opens. PB uses 60. */
  warnSeconds: number;
  /** Absolute cap from login, regardless of activity. */
  maxHours: number;
  /** Whether the warning chimes. `Session.WarnSound = N` silences it. */
  warnSound: boolean;
  /** The JWT's own lifetime — the TOKEN clock, not the SESSION clock. Two knobs, two jobs. */
  tokenTtlMinutes: number;
}

/** Mirrors the backend's own fallbacks (`SessionPolicy.DEFAULT_*`). */
export const SESSION_DEFAULTS: SessionFeatureFlags = {
  idleMinutes: 60,
  warnSeconds: 120,
  maxHours: 12,
  warnSound: true,
  tokenTtlMinutes: 20,
};

const DEFAULTS: FeatureFlags = {
  ancientDbEnabled: false,
  clientMode: 'STD',
  electronicSignatureEnabled: false,
  addRowcount: [],
  displayDateFormat: 'mm/dd/yyyy',
  // Chris's shipped values, identical to MaxResultsConfig.FALLBACK on the backend. Keeping the
  // two in lockstep is the POC1 "ACC vs DAR01" lesson: a FE default that quietly disagrees with
  // the BE default hides the bug instead of surfacing it.
  maxResults: { defaultValue: '500', options: ['100', '500', '1000', '5000', 'ALL'] },
  session: SESSION_DEFAULTS,
  testersHubEnabled: true,
  demoSystem: false,
};

export function useFeatures(): FeatureFlags & {
  session: SessionFeatureFlags;
  testersHubEnabled: boolean;
  demoSystem: boolean;
} {
  const q = useQuery({
    queryKey: ['config', 'features'],
    queryFn: () => api<FeatureFlags>('/api/config/features'),
    staleTime: Infinity,
    gcTime: Infinity,
  });
  // Until the request resolves (or if it fails), return safe defaults so
  // calling components don't have to deal with undefined.
  const flags = q.data ?? DEFAULTS;
  // slice-3560: `session` is non-optional on the way OUT. useSessionExpiry does arithmetic
  // with every field on the very first render — before this query resolves — and a partial
  // policy there would mean NaN timers, so each field falls back individually.
  return {
    ...flags,
    // slice-3609: absent on older backends / mocked payloads ⇒ hub stays visible.
    testersHubEnabled: flags.testersHubEnabled ?? true,
    demoSystem: flags.demoSystem ?? false,
    session: { ...SESSION_DEFAULTS, ...(flags.session ?? {}) },
  };
}

/**
 * BigList #8: syncs the resolved `displayDateFormat` into the `shared/format/pbDateFormat`
 * module singleton so `pbDate()`/`pbDateTime()` (which are plain functions, not hooks —
 * they're called from AG-Grid `valueFormatter`s and other non-component contexts that can't
 * use `useFeatures()` directly) pick up the tenant's setting. Mounted once, high in the
 * app shell (`App.tsx`), mirroring how PB resolves the cascade once at login and caches it
 * for the session. Re-fires on every features refetch, which is a no-op unless the tenant's
 * configured value actually changed.
 */
export function useSyncDisplayDateFormat(): void {
  const { displayDateFormat } = useFeatures();
  useEffect(() => {
    setDisplayDateFormat(displayDateFormat);
  }, [displayDateFormat]);
}

/**
 * slice-3609: the testers-hub switch, read from the features CACHE only — never fetches.
 * {@code useSyncDisplayDateFormat} (app shell, post-login) populates ['config','features'] once
 * per session, so every screen sees the real value; components that mount in isolation (unit
 * tests, storybook-style renders) see {@code true} and issue no extra request — which matters
 * because page tests drive ordered fetch mocks that an unexpected GET would shift.
 * {@code enabled: false} keeps the observer subscribed to cache updates without a fetch.
 */
export function useTestersHubEnabled(): boolean {
  const q = useQuery({
    queryKey: ['config', 'features'],
    queryFn: () => api<FeatureFlags>('/api/config/features'),
    enabled: false,
    staleTime: Infinity,
    gcTime: Infinity,
  });
  return q.data?.testersHubEnabled ?? true;
}

/**
 * slice-3626 (ADR-0050): is this the customer DEMO instance? Cache-only read of the same
 * features query (no fetch of its own — see {@link useTestersHubEnabled} for why). Defaults
 * to FALSE until the flags load, so demo-only nav/routes never flash on a non-demo box.
 */
export function useDemoSystemEnabled(): boolean {
  const q = useQuery({
    queryKey: ['config', 'features'],
    queryFn: () => api<FeatureFlags>('/api/config/features'),
    enabled: false,
    staleTime: Infinity,
    gcTime: Infinity,
  });
  return q.data?.demoSystem ?? false;
}

/**
 * slice-3930: is the TEMPORARY "My top screens" survey switched on for this instance
 * ({@code ph10.topscreens.enabled}, ph10-test only)? Cache-only read of the features query, like
 * {@link useDemoSystemEnabled}; defaults to FALSE so the nav link never flashes on MYRX.
 */
export function useTopScreensEnabled(): boolean {
  const q = useQuery({
    queryKey: ['config', 'features'],
    queryFn: () => api<FeatureFlags>('/api/config/features'),
    enabled: false,
    staleTime: Infinity,
    gcTime: Infinity,
  });
  return q.data?.topScreensEnabled ?? false;
}

/**
 * slice-4446 (John 26 Oct): this box's RF Simulator instance — PH10-Russ on demo.qssi-wms.com.
 * Cache-only read of the features query, like {@link useDemoSystemEnabled} (the RF Simulator
 * route is gated on the same cached flags, so the cache is loaded whenever the page can render).
 */
export function useRfSimInstance(): string | undefined {
  const q = useQuery({
    queryKey: ['config', 'features'],
    queryFn: () => api<FeatureFlags>('/api/config/features'),
    enabled: false,
    staleTime: Infinity,
    gcTime: Infinity,
  });
  return q.data?.rfSimInstance;
}

/**
 * slice-4482 (John 29 Oct): does this box show Activities > RF Simulator? Every demo box does;
 * a non-demo box only when {@code ph10.rf-simulator.enabled} is on (ph10-test — not MYRX, which
 * shares ph10-test's web root but has its own backend flags). Cache-only, defaults FALSE.
 */
export function useRfSimulatorEnabled(): boolean {
  const q = useQuery({
    queryKey: ['config', 'features'],
    queryFn: () => api<FeatureFlags>('/api/config/features'),
    enabled: false,
    staleTime: Infinity,
    gcTime: Infinity,
  });
  return (q.data?.demoSystem ?? false) || (q.data?.rfSimEnabled ?? false);
}

/** slice-4482: this box's RF Simulator Mover ID (B4A-001 on ph10-test). Cache-only. */
export function useRfSimMover(): string | undefined {
  const q = useQuery({
    queryKey: ['config', 'features'],
    queryFn: () => api<FeatureFlags>('/api/config/features'),
    enabled: false,
    staleTime: Infinity,
    gcTime: Infinity,
  });
  return q.data?.rfSimMover;
}
